import { useRef, useState } from 'react';
import { AudioPlayer } from '@ai-tutor/ui';
import { Podcast } from 'lucide-react';
import { mockAudioTranscriptData, mockLessonHeading } from '@/data/fixtures';
import { ComingSoon } from '@/components/core/common/feedback/ComingSoon';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';
import { useDataMode } from '@/contexts/DataModeContext';
import { usePodcastSectionParam } from '@/hooks/lesson/useTextSectionParam';
import {
  useLessonTitle,
  useSectionPodcasts,
} from '@/hooks/lesson/useSectionPodcasts';
import { PodcastSkeleton } from '../skeletons/PanelSkeletons';
import { PodcastLayout } from '../podcast/PodcastLayout';
import { SectionPodcastList } from '../podcast/SectionPodcastList';
import { SectionPodcastView } from '../podcast/SectionPodcastView';
import { PodcastTranscript } from './PodcastTranscript';

interface AudioRendererProps {
  lessonId?: string;
  onProgressUpdate?: (mediaType: string, progress: number) => void;
}

/**
 * The lesson's Podcast tab. Podcasts are made per section, on demand: the
 * tab lists the sections with theirs, and one opens its player and
 * transcript (`?episode=N`).
 */
export function AudioRenderer({
  lessonId,
  onProgressUpdate,
}: AudioRendererProps) {
  const { isApi, isEmpty } = useDataMode();

  if (isApi) {
    if (!lessonId) {
      return (
        <ComingSoon
          icon={Podcast}
          title='AI-Generated Podcasts'
          description='Open a lesson to listen to its podcasts.'
        />
      );
    }
    return (
      <SectionPodcasts
        lessonId={lessonId}
        onProgressUpdate={onProgressUpdate}
      />
    );
  }

  if (isEmpty) {
    return (
      <ComingSoon
        icon={Podcast}
        title='AI-Generated Podcasts'
        description='Listen to your lessons in podcast format. Coming soon!'
      />
    );
  }

  return <MockAudioRenderer onProgressUpdate={onProgressUpdate} />;
}

// ============================================================================
// API MODE - /lessons/{id}/podcasts and /podcasts/{n}
// ============================================================================

function SectionPodcasts({
  lessonId,
  onProgressUpdate,
}: {
  lessonId: string;
  onProgressUpdate?: (mediaType: string, progress: number) => void;
}) {
  const list = useSectionPodcasts(lessonId);
  const lessonTitle = useLessonTitle(lessonId);
  const { section: episode, open, close } = usePodcastSectionParam();
  const sections = list.data?.sections ?? [];

  if (list.isLoading && !list.data) return <PodcastSkeleton mode='fetching' />;
  if (sections.length === 0) {
    // The plan (its sections) is still being made; the list polls for it.
    return (
      <PanelFallback
        variant='step'
        title='Waiting for the lesson'
        description="Each section of the lesson gets its own podcast. They can be made once the lesson's sections are planned."
      />
    );
  }
  if (episode !== null && sections.some(s => s.index === episode)) {
    return (
      <SectionPodcastView
        lessonId={lessonId}
        lessonTitle={lessonTitle}
        sectionIndex={episode}
        total={sections.length}
        onBack={close}
        onProgressUpdate={onProgressUpdate}
      />
    );
  }
  return (
    <SectionPodcastList
      lessonId={lessonId}
      lessonTitle={lessonTitle}
      sections={sections}
      onOpen={index => open(index)}
    />
  );
}

// ============================================================================
// MOCK MODE - unchanged behavior so the existing mock data UX stays intact
// ============================================================================

function MockAudioRenderer({
  onProgressUpdate,
}: {
  onProgressUpdate?: (mediaType: string, progress: number) => void;
}) {
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const audioSeekRef = useRef<((time: number) => void) | null>(null);

  const handleAudioTimeUpdate = (time: number) => {
    setAudioCurrentTime(time);
    onProgressUpdate?.('audio', (time / 300) * 100); // assume 5min sample
  };

  return (
    <PodcastLayout title={mockLessonHeading.title} duration={300}>
      <AudioPlayer
        url='/sample-audio.mp3'
        onTimeUpdate={handleAudioTimeUpdate}
        onSeekReady={fn => (audioSeekRef.current = fn)}
      />
      <PodcastTranscript
        transcript={mockAudioTranscriptData}
        currentTime={audioCurrentTime}
        onSeek={t => audioSeekRef.current?.(t)}
      />
    </PodcastLayout>
  );
}
